import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth, UserButton } from '@clerk/clerk-react';
import FontStyles from '../components/shared/FontStyles.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import RootedWordmark from '../components/shared/RootedWordmark.jsx';
import {
  PAPER,
  INK,
  INK_MID,
  INK_SOFT,
  ACCENT,
} from '../components/shared/theme.js';
import { loadStudy, loadGuide } from '../services/storage.js';

export default function Welcome() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const existingStudy = loadStudy();
  const existingGuide = loadGuide();

  return (
    <div
      className="min-h-screen w-full ff-ui"
      style={{ background: PAPER, color: INK }}
    >
      <FontStyles />

      <div
        className="screen-in relative grain min-h-screen flex flex-col items-center"
        style={{ backgroundColor: PAPER }}
      >
        {isSignedIn && (
          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
            <UserButton afterSignOutUrl="/" />
          </div>
        )}
        <div className="flex-1 flex flex-col items-center px-9 pt-16 pb-10 text-center max-w-[440px] w-full">
          {/* Fox mark */}
          <div className="fox-glow">
            <FoxMark size={86} />
          </div>

          {/* Wordmark + publisher */}
          <div className="mt-6">
            <RootedWordmark size="lg" showPublisher={true} />
          </div>

          {/* Hairline */}
          <span className="block hairline-gold h-px w-32 mt-5" />

          {/* Tagline */}
          <div className="mt-7 max-w-[320px]">
            <p
              className="ff-display leading-[1.3]"
              style={{
                color: INK,
                fontSize: 22,
                fontWeight: 300,
                fontStyle: 'italic',
              }}
            >
              It's not about who you are.
              <br />
              <span style={{ color: ACCENT }}>It's who you are becoming.</span>
            </p>
          </div>

          {/* Subhead */}
          <p
            className="ff-display mt-7 leading-[1.5] max-w-[320px]"
            style={{ color: INK_MID, fontSize: 15, fontWeight: 400 }}
          >
            A personalized faith formation experience built entirely around
            your life, your season, and who you are becoming.
          </p>

          {/* Value props */}
          <div
            className="mt-10 space-y-3 ff-ui text-[13px] text-left max-w-[320px] w-full"
            style={{ color: INK_MID }}
          >
            {[
              'Completely private — your story goes nowhere',
              'Personalized to your Enneagram type and life season',
              '5 days free — no card required to start',
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-3">
                <span
                  className="mt-[7px] w-1 h-1 rounded-full flex-shrink-0"
                  style={{ background: ACCENT }}
                />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="px-7 pb-12 w-full max-w-[440px]">
          <button
            onClick={() => navigate('/guide-setup')}
            className="w-full py-4 rounded-full ff-ui font-medium text-[14px] flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            style={{ background: INK, color: PAPER }}
          >
            Begin your journey
            <ArrowRight size={16} strokeWidth={1.75} />
          </button>

          {existingStudy && (
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              <p
                className="ff-display italic"
                style={{
                  fontSize: 14,
                  color: INK,
                  opacity: 0.6,
                  marginBottom: 12,
                }}
              >
                Welcome back
                {existingGuide?.name
                  ? ` — ${existingGuide.name} is waiting for you`
                  : ''}
                .
              </p>
              <button
                onClick={() => navigate('/study')}
                style={{
                  background: 'transparent',
                  border: `1.5px solid ${ACCENT}`,
                  color: ACCENT,
                  padding: '12px 28px',
                  borderRadius: 24,
                  fontSize: 15,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Continue your study
              </button>
            </div>
          )}

          {!isSignedIn && (
            <div
              className="text-center mt-5 ff-ui text-[12px]"
              style={{ color: INK_SOFT }}
            >
              Already walking?{' '}
              <button
                onClick={() => navigate('/sign-in')}
                className="font-medium"
                style={{
                  color: ACCENT,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
